import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ReadinessBadge, InactivaBadge } from "@/components/admin/readiness";
import { calcEdad } from "@/lib/format";
import { Search, Plus, ChevronRight } from "lucide-react";

// GC94: el read-model INTERNO publica `nombre_completo` y el DTO externo autorizado publica
// `nombre`. La lista administrativa normaliza la PRESENTACIÓN (no el registro): el nombre real se
// muestra y se busca con cualquiera de los dos contratos.
const nombreDe = (p) => (p.nombre_completo || p.nombre || "").trim();

// Subtítulo construido SÓLO con los campos que el read-model autorizado realmente entrega. Un
// campo ausente del DTO (p. ej. `paquete`, que no viaja a roles institucionales) no se narra como
// carencia: antes todas las personas se leían «Sin paquete».
const detalleDe = (p, edad) => {
  const piezas = [];
  if (p.fecha_nacimiento) piezas.push(`${edad} años`);
  const ubic = p.habitacion || p.zona_nombre;
  if (ubic) piezas.push(ubic);
  // GC94: etiqueta REAL de la cama registrada (el modelo la guarda en `etiqueta`).
  if (p.cama_etiqueta) piezas.push(`Cama ${p.cama_etiqueta}`);
  if (Object.prototype.hasOwnProperty.call(p, "paquete")) piezas.push(p.paquete || "Sin paquete");
  return piezas.join(" · ");
};

const esIncompleto = (p) => {
  const est = p.readiness?.estado;
  return est && est !== "listo" && est !== "suspendido";
};

// Lista administrativa COMPACTA de personas de un sitio (v2.9.9). OPERACIÓN DIARIA = personas
// ACTIVAS. Las bajas históricas (inactivas) quedan persistidas y accesibles vía filtro, pero NO
// cuentan en readiness/totales operativos ni aparecen por defecto. Búsqueda + filtro + orden.
export default function PersonasTab({ personas, onOpen, onAdd, puedeAgregar = true }) {
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("activos");
  const [orden, setOrden] = useState("nombre");

  const activas = personas.filter((p) => p.estado !== "Inactiva");
  const inactivas = personas.filter((p) => p.estado === "Inactiva");
  const listos = activas.filter((p) => p.readiness?.estado === "listo").length;
  const incompletos = activas.filter(esIncompleto).length;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    // Base del filtro: inactivos parte de bajas; todos incluye ambos; el resto parte de activas.
    let base;
    if (filtro === "inactivos") base = inactivas;
    else if (filtro === "todos") base = personas;
    else base = activas; // activos, listos, incompletos: SOLO sobre personas activas
    let arr = base.filter((p) => {
      if (t && !nombreDe(p).toLowerCase().includes(t)) return false;
      if (filtro === "listos" && p.readiness?.estado !== "listo") return false;
      if (filtro === "incompletos" && !esIncompleto(p)) return false;
      return true;
    });
    arr = [...arr].sort((a, b) => {
      if (orden === "readiness") {
        const rank = (p) => (p.estado === "Inactiva" ? -1 : p.readiness?.estado === "listo" ? 2 : p.readiness?.estado === "suspendido" ? 1 : 0);
        return rank(a) - rank(b);
      }
      return nombreDe(a).localeCompare(nombreDe(b));
    });
    return arr;
  }, [q, personas, activas, inactivas, filtro, orden]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-medium text-[#012472]">Personas</h2>
          <span data-testid="personas-resumen" className="text-sm text-slate-500">
            <b className="text-emerald-600" title="Readiness integral: expediente, cobertura técnica y plan de respuesta completos.">{listos} listos para monitoreo</b> · <b className={incompletos > 0 ? "text-amber-600" : "text-slate-500"}>{incompletos} incompletos</b>
            <span className="text-slate-400"> · {activas.length} total</span>
            {inactivas.length > 0 && <span className="text-slate-400"> · {inactivas.length} inactiva(s)</span>}
          </span>
        </div>
        {puedeAgregar && (
        <Button data-testid="add-person-button" onClick={onAdd} size="sm" className="rounded-lg bg-[#01CBE3] hover:bg-[#01B5CB] text-white">
          <Plus strokeWidth={1.75} className="h-4 w-4 mr-1" /> Agregar persona
        </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="persona-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar persona…" className="pl-10 rounded-xl bg-white" />
        </div>
        <Select value={filtro} onValueChange={setFiltro}>
          <SelectTrigger data-testid="persona-filter-readiness" className="rounded-xl bg-white w-[170px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="activos">Activos</SelectItem>
            <SelectItem value="listos">Listos</SelectItem>
            <SelectItem value="incompletos">Incompletos</SelectItem>
            <SelectItem value="inactivos">Inactivos</SelectItem>
            <SelectItem value="todos">Todos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={orden} onValueChange={setOrden}>
          <SelectTrigger data-testid="persona-sort" className="rounded-xl bg-white w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="nombre">Orden: nombre</SelectItem>
            <SelectItem value="readiness">Orden: readiness</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <p data-testid="personas-empty" className="text-sm text-slate-400 py-6 text-center">No hay personas que coincidan.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {filtered.map((p) => {
            const inactiva = p.estado === "Inactiva";
            const nombre = nombreDe(p);
            const detalle = detalleDe(p, p.fecha_nacimiento ? calcEdad(p.fecha_nacimiento) : null);
            return (
              <button key={p.id} data-testid={`persona-row-${p.id}`} onClick={() => onOpen(p)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50/70 transition-colors group ${inactiva ? "opacity-70" : ""}`}>
                <span className={`h-9 w-9 rounded-full text-sm flex items-center justify-center font-semibold shrink-0 ${inactiva ? "bg-slate-100 text-slate-400" : "bg-[#012472]/10 text-[#012472]"}`}>
                  {(nombre || "?").charAt(0).toUpperCase()}
                </span>
                {/* GC94 · móvil: el nombre COMPLETO se lee (salta de línea) y el chip de estado se
                    acomoda debajo en pantallas angostas. No se recorta a «LAB Bru…». */}
                <div className="min-w-0 flex-1 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <div className="min-w-0">
                    <p data-testid={`persona-nombre-${p.id}`} title={nombre}
                      className="text-sm font-medium text-[#012472] break-words [overflow-wrap:anywhere]">
                      {nombre || "(sin nombre)"}
                    </p>
                    {detalle && (
                      <p data-testid={`persona-detalle-${p.id}`} className="text-xs text-slate-400 break-words">{detalle}</p>
                    )}
                  </div>
                  <span className="shrink-0">
                    {inactiva
                      ? <InactivaBadge testId={`persona-inactiva-badge-${p.id}`} />
                      : (p.readiness && <ReadinessBadge estado={p.readiness.estado} testId={`persona-readiness-badge-${p.id}`} />)}
                  </span>
                </div>
                <ChevronRight strokeWidth={1.5} className="mt-1 h-4 w-4 text-slate-300 group-hover:text-[#01CBE3] transition-colors shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
